<template>
  <div class="md-layout">
    <div class="md-layout-item md-size-100">
      <md-card>
        <md-card-header class="md-card-header-icon md-card-header-green">
          <div class="card-icon">
            <span class="material-symbols-outlined">assignment_return</span>
          </div>
          <h4 class="title">{{ $t("layout.returnsOverview") }}</h4>
        </md-card-header>

        <md-card-content>
          <md-table v-model="returns.mdData" table-header-color="green" class="table-sticky">
            <md-table-row slot="md-table-row" slot-scope="{ item }" :class="{ 'ret-row--completed': item.completedOn }">
              <md-table-cell :md-label="$t('returns.table.actions')">
                <!-- ret-action: kleiner Abstand zwischen den Buttons (Karte 21.08., Punkt 5) -->
                <md-button class="md-icon-button ret-action" @click="openDetail(item)"><md-icon>visibility</md-icon></md-button>
                <md-button v-if="canComplete && !item.completedOn" class="md-icon-button ret-action" @click="askComplete(item.id, false)">
                  <md-icon>check</md-icon>
                  <md-tooltip md-direction="top">{{ $t("returns.detail.complete") }}</md-tooltip>
                </md-button>
              </md-table-cell>
              <!-- Allgemeiner Status (angelegt/erledigt) - unabhaengig vom Lagerstatus (Kundenwunsch 18.08., P14) -->
              <!-- Kompakt-Konvention wie im Lieferplan: Status als Icon + Mouseover statt Text-Chip -->
              <md-table-cell :md-label="$t('returns.table.status')">
                <md-icon class="ret-statusicon" :class="item.completedOn ? 'ret-statusicon--completed' : 'ret-statusicon--created'">
                  {{ item.completedOn ? "check_circle" : "schedule" }}
                  <md-tooltip md-direction="top">{{ item.completedOn ? $t("returns.generalStatus.completed") : $t("returns.generalStatus.created") }}</md-tooltip>
                </md-icon>
              </md-table-cell>
              <md-table-cell :md-label="$t('returns.table.warehouseStatus')">
                <md-icon class="ret-statusicon" :class="'ret-statusicon--' + item.status">
                  {{ statusIcons[item.status] || "help_outline" }}
                  <md-tooltip md-direction="top">{{ $t("returns.statuses." + item.status) }}</md-tooltip>
                </md-icon>
              </md-table-cell>
              <md-table-cell :md-label="$t('returns.table.type')">
                <md-icon class="ret-typeicon">
                  {{ typeIcons[item.returnType] || typeIcons.other }}
                  <md-tooltip md-direction="top">{{ $t("returns.types." + item.returnType) }}</md-tooltip>
                </md-icon>
              </md-table-cell>
              <!-- Marktplatz-Icon (P5) - Platzhalter-Badge, bis der Kunde Icon-Dateien liefert -->
              <md-table-cell :md-label="$t('returns.table.marketplace')">
                <span v-if="marketplace(item)" class="ret-mp" :style="{ background: marketplace(item).color }">
                  {{ marketplace(item).short }}
                  <md-tooltip md-direction="top">{{ marketplace(item).label }}</md-tooltip>
                </span>
                <span v-else class="text-gray">–</span>
              </md-table-cell>
              <!-- Paketdienstleister der Ruecksendung (P18) -->
              <md-table-cell :md-label="$t('returns.table.carrier')">
                <span v-if="carrier(item)" class="ret-mp" :style="{ background: carrier(item).color, color: carrier(item).text }">
                  {{ carrier(item).short }}
                  <md-tooltip md-direction="top">{{ carrier(item).label }}</md-tooltip>
                </span>
                <span v-else class="text-gray">–</span>
              </md-table-cell>
              <!-- ID + Rechnungsnummer + Bestellnummer(n) untereinander (P2/3/4/6/13) -->
              <md-table-cell :md-label="$t('returns.table.invoice')">
                <div class="ret-idline">#{{ item.id }}</div>
                <div v-if="item.invoiceNumber" class="ret-refline">
                  <!-- Rechnungsnummer oeffnet die Afterbuy-SUCHE in einem neuen Fenster (P13/13.1) -->
                  <a href="#" class="invoice-link" @click.prevent="openInvoiceSearch(item.invoiceNumber)">{{ item.invoiceNumber }}</a>
                  <button class="ret-copy" :title="$t('returns.table.copy')" @click.stop="copy(item.invoiceNumber)"><md-icon>content_copy</md-icon></button>
                </div>
                <span v-else class="text-gray">–</span>
                <div v-if="item.platformOrderId" class="ret-refline">
                  <a v-if="orderUrl(item.marketplace, item.platformOrderId)" :href="orderUrl(item.marketplace, item.platformOrderId)" target="_blank" rel="noopener" class="invoice-link">
                    {{ short(item.platformOrderId) }}
                  </a>
                  <span v-else>{{ short(item.platformOrderId) }}</span>
                  <button class="ret-copy" :title="$t('returns.table.copy')" @click.stop="copy(item.platformOrderId)"><md-icon>content_copy</md-icon></button>
                  <md-tooltip md-direction="top">{{ $t("returns.table.platformOrder") }}: {{ item.platformOrderId }}</md-tooltip>
                </div>
                <div v-if="item.secondaryOrderId" class="ret-refline">
                  <a v-if="shopifyUrl(item.secondaryOrderId)" :href="shopifyUrl(item.secondaryOrderId)" target="_blank" rel="noopener" class="invoice-link">{{ short(item.secondaryOrderId) }}</a>
                  <span v-else>{{ short(item.secondaryOrderId) }}</span>
                  <button class="ret-copy" :title="$t('returns.table.copy')" @click.stop="copy(item.secondaryOrderId)"><md-icon>content_copy</md-icon></button>
                  <md-tooltip md-direction="top">{{ $t("returns.table.shopOrder") }}: {{ item.secondaryOrderId }}</md-tooltip>
                </div>
              </md-table-cell>
              <md-table-cell :md-label="$t('returns.table.sender')">
                <!-- Feste Breite mit Ellipsis, volle Anschrift im Mouseover (Kompakt-Konvention) -->
                <div v-if="item.senderName || item.senderStreet || item.senderTown" class="ret-sender">
                  <strong v-if="item.senderName" class="ret-sender__line">{{ item.senderName }}</strong>
                  <span v-if="item.senderStreet" class="ret-sender__line">{{ item.senderStreet }}</span>
                  <span v-if="item.senderZipcode || item.senderTown" class="ret-sender__line">{{ item.senderZipcode }} {{ item.senderTown }}</span>
                  <md-tooltip md-direction="top">{{ item.senderName }} · {{ item.senderStreet }} · {{ item.senderZipcode }} {{ item.senderTown }}</md-tooltip>
                </div>
                <span v-else class="text-gray">–</span>
              </md-table-cell>
              <!-- Beide Zeilen gleich aufgebaut, damit die Nummern untereinander stehen statt zu versetzen -->
              <md-table-cell :md-label="$t('returns.table.reference')" class="ret-refcell">
                <div v-if="item.referenceNumber" class="ret-refline">
                  <span class="ret-refline__val">
                    <md-icon>receipt_long</md-icon><span>{{ short(item.referenceNumber) }}</span>
                    <md-tooltip md-direction="top">{{ $t("returns.detail.reference") }}: {{ item.referenceNumber }}</md-tooltip>
                  </span>
                  <button class="ret-copy" :title="$t('returns.table.copy')" @click.stop="copy(item.referenceNumber)"><md-icon>content_copy</md-icon></button>
                </div>
                <div v-if="item.returnTrackingNumber" class="ret-refline ret-refline--muted">
                  <span class="ret-refline__val">
                    <md-icon>local_shipping</md-icon><span>{{ short(item.returnTrackingNumber) }}</span>
                    <md-tooltip md-direction="top">{{ $t("returns.detail.returnTracking") }}: {{ item.returnTrackingNumber }}</md-tooltip>
                  </span>
                  <button class="ret-copy" :title="$t('returns.table.copy')" @click.stop="copy(item.returnTrackingNumber)"><md-icon>content_copy</md-icon></button>
                </div>
                <span v-if="!item.referenceNumber && !item.returnTrackingNumber" class="text-gray">–</span>
              </md-table-cell>
              <md-table-cell :md-label="$t('returns.table.order')">
                <div v-if="item.customerOrderId" class="ret-refline">
                  <span class="ret-refline__val">
                    {{ short(item.customerOrderId) }}
                    <md-tooltip md-direction="top">
                      {{ item.customerOrderId }}<template v-if="item.afterbuyPlatform"> · {{ item.afterbuyPlatform }}</template>
                    </md-tooltip>
                  </span>
                  <button class="ret-copy" :title="$t('returns.table.copy')" @click.stop="copy(item.customerOrderId)"><md-icon>content_copy</md-icon></button>
                  <span v-if="item.afterbuyMemo" class="material-symbols-outlined ret-memoicon">sticky_note_2
                    <md-tooltip md-direction="top"><div class="ret-tip-memo">{{ item.afterbuyMemo }}</div></md-tooltip>
                  </span>
                </div>
                <span v-else class="text-gray">–</span>
              </md-table-cell>
              <md-table-cell :md-label="$t('returns.table.reklaImages')">
                <!-- Reklamationsbilder zur Rechnungsnummer (Trello 21.08.) -->
                <md-icon v-if="item.reklaImageCount > 0" class="rekla-icon" @click.native="openReklaImages(item)">
                  image
                  <md-tooltip md-direction="top">{{ item.reklaImageCount }} {{ $t("orders.images.tooltip") }}</md-tooltip>
                </md-icon>
                <span v-else class="text-gray">–</span>
              </md-table-cell>
              <md-table-cell :md-label="$t('returns.table.purchased')">
                <template v-if="item.orderSkus && item.orderSkus.length">
                  <div v-for="(s, i) in item.orderSkus.slice(0, 2)" :key="'p' + i" class="ret-sku">{{ s.quantity }}× {{ s.sku }}</div>
                  <span v-if="item.orderSkus.length > 2" class="ret-more">
                    +{{ item.orderSkus.length - 2 }}
                    <md-tooltip md-direction="top">
                      <table class="ret-tip">
                        <tr v-for="(s, i) in item.orderSkus" :key="'pt' + i">
                          <td class="text-right">{{ s.quantity }}×</td>
                          <td>{{ s.sku }}</td>
                        </tr>
                      </table>
                    </md-tooltip>
                  </span>
                </template>
                <span v-else class="text-gray">–</span>
              </md-table-cell>
              <md-table-cell :md-label="$t('returns.table.returned')">
                <template v-if="item.items && item.items.length">
                  <!-- Zustand ausgeschrieben mit Flaechenfuellung (Kundenwunsch 18.08., P15);
                       der Gebucht-Haken ist weg - der Lagerplatz steht jetzt in der WMS-Spalte (P16) -->
                  <div v-for="(s, i) in item.items.slice(0, 2)" :key="'r' + i" class="ret-sku ret-sku--filled" :class="'ret-fill--' + String(s.condition || 'A').toLowerCase()">
                    {{ s.quantity }}× {{ s.sku }}
                    <span class="ret-cond" :class="'ret-cond--' + String(s.condition || 'A').toLowerCase()">{{ conditionLabel(s.condition) }}</span>
                  </div>
                  <span class="ret-more">
                    <span v-if="item.items.length > 2">+{{ item.items.length - 2 }}</span>
                    <md-tooltip md-direction="top">
                      <table class="ret-tip">
                        <tr v-for="(s, i) in item.items" :key="'rt' + i">
                          <td class="text-right">{{ s.quantity }}×</td>
                          <td>{{ s.sku }}</td>
                          <td>{{ conditionLabel(s.condition) }}</td>
                          <td>{{ s.warehouseInventoryId ? $t("returns.booking.alreadyBooked") : "–" }}</td>
                        </tr>
                      </table>
                    </md-tooltip>
                  </span>
                </template>
                <span v-else class="text-gray">–</span>
              </md-table-cell>
              <md-table-cell :md-label="$t('returns.table.photos')">
                <md-icon class="ret-photoicon" :class="item.missingRequiredPhotos ? 'ret-warn' : 'ret-ok'" @click.native="openPhotos(item)">
                  {{ item.missingRequiredPhotos ? "photo_camera_back" : "photo_camera" }}
                  <md-tooltip md-direction="top">
                    {{ $tc("returns.table.photoCount", item.photoCount, { count: item.photoCount }) }}
                    <template v-if="item.missingRequiredPhotos"> – {{ $t("returns.summary.photosMissing") }}</template>
                  </md-tooltip>
                </md-icon>
              </md-table-cell>
              <md-table-cell :md-label="$t('returns.table.wms')">
                <!-- Lagerplatz direkt sichtbar + gruener Haken (Kundenwunsch 18.08., P16);
                     Artikel und Menge stehen schon in der Zurueck-Spalte -->
                <template v-if="bookedItems(item).length">
                  <div v-for="(loc, i) in bookedLocations(item)" :key="'wl' + i" class="ret-wms">
                    <span>{{ loc }}</span>
                    <md-icon class="ret-ok">check_circle</md-icon>
                  </div>
                </template>
                <span v-else class="text-gray">–</span>
              </md-table-cell>
              <md-table-cell :md-label="$t('returns.table.notes')">
                <span v-if="item.notes" class="ret-note">
                  <span class="material-symbols-outlined ret-memoicon">edit_note</span>
                  <md-tooltip md-direction="top"><div class="ret-tip-memo">{{ item.notes }}</div></md-tooltip>
                </span>
                <span v-else class="text-gray">–</span>
              </md-table-cell>
              <!-- Avatar oben, Datum darunter; Name und Uhrzeit auf die Sekunde im Mouseover -->
              <md-table-cell :md-label="$t('returns.table.createdOn')">
                <div class="ret-user">
                  <!-- UserAvatar rendert nichts, wenn kein Profilbild hinterlegt ist - darunter liegen die Initialen -->
                  <md-avatar v-if="item.createdById" class="ret-avatar">
                    <span class="ret-avatar__initials">{{ initials(item.createdBy) }}</span>
                    <user-avatar :user-id="item.createdById" alt="" />
                  </md-avatar>
                  <span class="ret-user__date">{{ item.createdOn | formatDate }}</span>
                  <md-tooltip md-direction="top">{{ item.createdBy }} · {{ item.createdOn | formatDateTimeSeconds }}</md-tooltip>
                </div>
              </md-table-cell>
              <md-table-cell :md-label="$t('returns.table.completed')">
                <div v-if="item.completedOn" class="ret-user">
                  <md-avatar v-if="item.completedById" class="ret-avatar">
                    <span class="ret-avatar__initials">{{ initials(item.completedBy) }}</span>
                    <user-avatar :user-id="item.completedById" alt="" />
                  </md-avatar>
                  <span class="ret-user__date">{{ item.completedOn | formatDate }}</span>
                  <md-tooltip md-direction="top">{{ item.completedBy }} · {{ item.completedOn | formatDateTimeSeconds }}</md-tooltip>
                </div>
                <span v-else class="text-gray">–</span>
              </md-table-cell>
            </md-table-row>
          </md-table>

          <md-pagination :md-update="updatePagination" :md-data.sync="returns" :md-page-size="filters.pageSize" :md-page-options="[10, 20, 50, 100]" />
        </md-card-content>
      </md-card>
    </div>

    <!-- Detail -->
    <modal v-if="detailModal" @close="closeDetail" class-name="returns-modal-container">
      <template slot="header">
        <h4 class="modal-title">
          {{ $t("returns.title") }} #{{ detail.id }}
          <span class="ret-status" :class="'ret-status--' + detail.status">{{ $t("returns.statuses." + detail.status) }}</span>
        </h4>
        <md-button class="md-simple md-just-icon md-round modal-default-button" @click="closeDetail">
          <md-icon>clear</md-icon>
        </md-button>
      </template>

      <template slot="body">
        <div v-if="detailLoading" class="text-center" style="padding: 24px">
          <md-progress-spinner md-mode="indeterminate" :md-diameter="40"></md-progress-spinner>
        </div>

        <div v-else-if="detail.id" class="ret-detail">
          <div class="md-layout">
            <div class="md-layout-item md-size-50">
              <h5 class="ret-detail__heading">{{ $t("returns.detail.data") }}</h5>
              <table class="ret-detail__table">
                <tr>
                  <th>{{ $t("returns.table.type") }}</th>
                  <td>{{ $t("returns.types." + detail.returnType) }}</td>
                </tr>
                <tr v-if="detail.invoiceNumber">
                  <th>{{ $t("returns.detail.invoice") }}</th>
                  <td>
                    <a v-if="detail.invoiceNumber" href="#" class="invoice-link" @click.prevent="openInvoiceSearch(detail.invoiceNumber)">{{ detail.invoiceNumber }}</a>
                    <strong v-else>{{ detail.invoiceNumber }}</strong>
                  </td>
                </tr>
                <tr v-if="detail.referenceNumber">
                  <th>{{ $t("returns.detail.reference") }}</th>
                  <td>{{ detail.referenceNumber }}</td>
                </tr>
                <tr v-if="detail.returnTrackingNumber">
                  <th>{{ $t("returns.detail.returnTracking") }}</th>
                  <td>{{ detail.returnTrackingNumber }}</td>
                </tr>
                <!-- Paketdienstleister: von der Label-Erkennung befuellt, hier aenderbar (P18) -->
                <tr>
                  <th>{{ $t("returns.table.carrier") }}</th>
                  <td>
                    <select v-if="canEdit" v-model="detail.carrier" class="ret-carrier-select" @change="saveCarrier">
                      <option :value="null">–</option>
                      <option v-for="c in carrierList" :key="c.id" :value="c.id">{{ c.name }}</option>
                    </select>
                    <template v-else>{{ carrier(detail) ? carrier(detail).label : "–" }}</template>
                  </td>
                </tr>
                <tr v-if="detail.productSku || detail.productEan">
                  <th>{{ $t(detail.productSku ? "returns.detail.productSku" : "returns.detail.productEan") }}</th>
                  <td>
                    {{ detail.productSku || detail.productEan }}<small v-if="detail.productSku && detail.productEan"> · EAN {{ detail.productEan }}</small>
                  </td>
                </tr>
                <tr v-if="detail.senderName">
                  <th>{{ $t("returns.detail.sender") }}</th>
                  <td>{{ detail.senderName }}<br v-if="detail.senderStreet" />{{ detail.senderStreet }}<br />{{ detail.senderZipcode }} {{ detail.senderTown }}</td>
                </tr>
                <tr>
                  <th>{{ $t("returns.summary.order") }}</th>
                  <td>
                    <template v-if="detail.customerOrderId || detail.afterbuyOrderId">
                      {{ detail.customerOrderId || detail.afterbuyOrderId }}
                      <small v-if="detail.afterbuyPlatform"> · {{ detail.afterbuyPlatform }}</small>
                    </template>
                    <em v-else>{{ $t("returns.summary.noOrder") }}</em>
                    <md-button v-if="canEdit && detail.status !== 'booked'" class="md-simple md-just-icon" @click="openAssign">
                      <md-icon>search</md-icon>
                    </md-button>
                  </td>
                </tr>
              </table>
              <p v-if="detail.afterbuyMemo" class="ret-detail__memo">{{ detail.afterbuyMemo }}</p>

              <h5 class="ret-detail__heading">{{ $t("returns.detail.notes") }}</h5>
              <md-field class="ret-notesfield">
                <md-textarea v-model="notesDraft" :disabled="!canEdit" md-autogrow></md-textarea>
              </md-field>
              <md-button v-if="canEdit" class="md-success md-sm" :disabled="saving || notesDraft === (detail.notes || '')" @click="saveNotes">
                {{ $t("returns.detail.notesSave") }}
              </md-button>
            </div>

            <div class="md-layout-item md-size-50">
              <h5 class="ret-detail__heading">{{ $t("returns.detail.timeline") }}</h5>
              <ul class="ret-timeline">
                <li v-for="(e, i) in timeline" :key="i">
                  <md-icon>{{ e.icon }}</md-icon>
                  <span>{{ e.label }}</span>
                  <small>{{ e.on | formatDateTime }}<template v-if="e.by"> · {{ e.by }}</template></small>
                </li>
              </ul>
            </div>
          </div>

          <h5 class="ret-detail__heading">{{ $t("returns.detail.photos") }}</h5>
          <return-photo-gallery :return-id="detail.id" :photos="detail.photos" />

          <h5 class="ret-detail__heading">{{ $t("returns.detail.purchased") }}</h5>
          <table v-if="detail.orderSkus && detail.orderSkus.length" class="ret-items">
            <thead>
              <tr>
                <th>{{ $t("returns.items.sku") }}</th>
                <th class="text-right">{{ $t("returns.items.quantity") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(s, i) in detail.orderSkus" :key="'os' + i">
                <td>{{ s.sku }}</td>
                <td class="text-right">{{ s.quantity }}</td>
              </tr>
            </tbody>
          </table>
          <p v-else class="ret-detail__empty">{{ $t("returns.detail.purchasedEmpty") }}</p>

          <h5 class="ret-detail__heading">{{ $t("returns.items.title") }}</h5>
          <table v-if="detail.items && detail.items.length" class="ret-items">
            <thead>
              <tr>
                <th>{{ $t("returns.items.sku") }}</th>
                <th class="text-right">{{ $t("returns.items.quantity") }}</th>
                <th>{{ $t("returns.detail.condition") }}</th>
                <th>{{ $t("returns.detail.targetSku") }}</th>
                <th>{{ $t("returns.detail.booked") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="i in detail.items" :key="i.id">
                <td>{{ i.sku }}</td>
                <td class="text-right">{{ i.quantity }}</td>
                <td>{{ conditionLabel(i.condition) }}</td>
                <td>{{ i.targetSku }}</td>
                <td>
                  <template v-if="i.warehouseInventoryId">
                    <md-icon class="ret-ok" :title="$t('returns.detail.bookedOn', { date: $options.filters.formatDateTime(i.bookedOn) })">check_circle</md-icon>
                    <span v-if="i.storageLocation"> {{ i.storageLocation }}</span>
                  </template>
                  <span v-else-if="i.condition === 'S' || i.condition === 'U'" class="text-gray">{{ $t("returns.booking.notBooked") }}</span>
                  <span v-else>–</span>
                </td>
              </tr>
            </tbody>
          </table>
          <p v-else class="ret-detail__empty">{{ $t("returns.items.empty") }}</p>
        </div>
      </template>

      <template slot="footer">
        <md-button class="md-secondary mr-1" @click="closeDetail">{{ $t("returns.detail.close") }}</md-button>
        <md-button v-if="canComplete && !detail.completedOn" class="md-info mr-1" @click="askComplete(detail.id, true)">
          {{ $t("returns.detail.complete") }}
        </md-button>
        <md-button v-if="canBook && detail.status !== 'booked' && detail.items && detail.items.length" class="md-success" @click="openRestock">
          {{ $t("returns.booking.title") }}
        </md-button>
      </template>
    </modal>

    <!-- Einbuchen -->
    <modal v-if="restockModal" @close="restockModal = false" class-name="returns-modal-container">
      <template slot="header">
        <h4 class="modal-title">{{ $t("returns.booking.title") }} – #{{ detail.id }}</h4>
        <md-button class="md-simple md-just-icon md-round modal-default-button" @click="restockModal = false">
          <md-icon>clear</md-icon>
        </md-button>
      </template>

      <template slot="body">
        <p v-if="bookingWarning" class="ret-warnbox">{{ $t("returns.booking.alreadyWarning") }}</p>

        <table class="ret-items">
          <thead>
            <tr>
              <th>{{ $t("returns.items.sku") }}</th>
              <th class="text-right">{{ $t("returns.items.quantity") }}</th>
              <th>{{ $t("returns.detail.condition") }}</th>
              <th>{{ $t("returns.detail.targetSku") }}</th>
              <th>{{ $t("returns.booking.location") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in restockRows" :key="row.id">
              <td>{{ row.sku }}</td>
              <td class="text-right">{{ row.quantity }}</td>
              <td>{{ conditionLabel(row.condition) }}</td>
              <td>{{ row.targetSku }}</td>
              <td>
                <span v-if="row.warehouseInventoryId" class="ret-ok-text">{{ $t("returns.booking.alreadyBooked") }}</span>
                <span v-else-if="row.condition === 'S' || row.condition === 'U'" class="text-gray">{{ $t("returns.booking.notBooked") }}</span>
                <md-select v-else v-model="row.storageLocationId">
                  <md-option v-for="l in bookingLocations" :key="l.id" :value="l.id">{{ l.display }}</md-option>
                </md-select>
              </td>
            </tr>
          </tbody>
        </table>
        <p class="ret-detail__empty">{{ $t("returns.booking.desktopHint") }}</p>
      </template>

      <template slot="footer">
        <md-button class="md-secondary mr-1" @click="restockModal = false">{{ $t("returns.detail.close") }}</md-button>
        <md-button class="md-success" :disabled="saving || !openRestockRows.length" @click="book">{{ $t("returns.booking.book") }}</md-button>
      </template>
    </modal>

    <!-- Zuordnung aendern -->
    <modal v-if="assignModal" @close="assignModal = false" class-name="returns-modal-container">
      <template slot="header">
        <h4 class="modal-title">{{ $t("returns.match.title") }} – #{{ detail.id }}</h4>
        <md-button class="md-simple md-just-icon md-round modal-default-button" @click="assignModal = false">
          <md-icon>clear</md-icon>
        </md-button>
      </template>

      <template slot="body">
        <div class="md-layout">
          <div class="md-layout-item md-size-35">
            <md-field>
              <label>{{ $t("returns.match.reference") }}</label>
              <md-input v-model="search.referenceNumber" @keyup.enter="findCandidates"></md-input>
            </md-field>
          </div>
          <div class="md-layout-item md-size-25">
            <md-field>
              <label>{{ $t("returns.match.returnTracking") }}</label>
              <md-input v-model="search.returnTracking" @keyup.enter="findCandidates"></md-input>
            </md-field>
          </div>
          <div class="md-layout-item md-size-25">
            <md-field>
              <label>{{ $t("returns.match.senderName") }}</label>
              <md-input v-model="search.senderName" @keyup.enter="findCandidates"></md-input>
            </md-field>
          </div>
          <div class="md-layout-item md-size-15">
            <md-field>
              <label>{{ $t("returns.match.senderZipcode") }}</label>
              <md-input v-model="search.senderZipcode" @keyup.enter="findCandidates"></md-input>
            </md-field>
          </div>
        </div>
        <md-button class="md-success md-sm" :disabled="matching" @click="findCandidates">{{ $t("returns.match.search") }}</md-button>
        <md-button class="md-simple md-sm" :disabled="!!extracting" @click="runExtract('label')">
          <md-icon>auto_awesome</md-icon>&nbsp;{{ extracting === "label" ? $t("returns.extract.running") : $t("returns.extract.button") }}
        </md-button>
        <md-button class="md-simple md-sm" :disabled="!!extracting" @click="runExtract('ean')">
          <md-icon>qr_code_2</md-icon>&nbsp;{{ extracting === "ean" ? $t("returns.extract.runningEan") : $t("returns.extract.buttonEan") }}
        </md-button>
        <p v-if="extractHint" class="ret-extract-hint">{{ extractHint }}</p>
        <p v-if="duplicates.length" class="ret-dup">{{ $t("returns.duplicate.warning", { list: duplicateList }) }} {{ $t("returns.duplicate.hint") }}</p>

        <div v-if="diagnosis.length" class="ret-diag" :class="{ 'ret-diag--hit': hasExactHit }">
          <strong>{{ $t(hasExactHit ? "returns.match.diagnosisTitleHit" : "returns.match.diagnosisTitle") }}</strong>
          <ul>
            <li v-for="(d, i) in diagnosis" :key="i">
              {{ $t("returns.match.diagnosis." + d.key) }} → {{ $t("returns.match.diagnosis." + d.outcome) }}
              <template v-if="d.hitCount">({{ d.hitCount }})</template>
              <template v-if="d.detail"> – {{ d.detail }}</template>
            </li>
          </ul>
        </div>

        <p v-if="searched && !candidates.length" class="ret-detail__empty">{{ $t("returns.match.noCandidates") }}</p>

        <table v-if="candidates.length" class="ret-items ret-cands">
          <thead>
            <tr>
              <th class="text-right">Score</th>
              <th>{{ $t("returns.summary.order") }}</th>
              <th>{{ $t("returns.match.senderName") }}</th>
              <th>{{ $t("returns.items.sku") }}</th>
              <th>{{ $t("returns.match.matched") }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="c in candidates" :key="c.source + (c.orderId || c.afterbuyOrderId)">
              <td class="text-right">
                <span class="ret-score" :class="scoreClass(c.score)">{{ c.score }} %</span>
              </td>
              <td>
                {{ c.customerOrderId || c.afterbuyOrderId }}
                <template v-if="c.invoiceNumber"
                  ><br /><small>{{ $t("returns.table.invoice") }}: {{ c.invoiceNumber }}</small></template
                >
                <template v-if="c.platformOrderId"
                  ><br /><small>{{ $t("returns.table.platformOrder") }}: {{ c.platformOrderId }}</small></template
                >
                <br />
                <span v-if="marketplace(c)" class="ret-mp" :style="{ background: marketplace(c).color }">{{ marketplace(c).short }}</span>
                <small v-else>{{ c.platform }}</small>
              </td>
              <td>
                {{ c.customerName }}<br /><small>{{ c.zipcode }} {{ c.town }}</small>
              </td>
              <td>{{ (c.skus || []).map((s) => s.quantity + "× " + s.sku).join(", ") }}</td>
              <td>
                <span v-for="f in c.matchedFields" :key="f" class="ret-field ret-field--ok">{{ f }}</span>
                <span v-for="f in c.mismatchedFields" :key="'x' + f" class="ret-field ret-field--bad">{{ f }}</span>
              </td>
              <td>
                <md-button class="md-success md-sm" :disabled="saving" @click="assign(c)">{{ $t("returns.match.confirm") }}</md-button>
              </td>
            </tr>
          </tbody>
        </table>
      </template>

      <template slot="footer">
        <md-button class="md-secondary" @click="assignModal = false">{{ $t("returns.detail.close") }}</md-button>
      </template>
    </modal>

    <!-- Nur Fotos: aus der Uebersicht soll das Kamerasymbol direkt zu den Bildern fuehren,
         nicht ins vollstaendige Detail -->
    <modal v-if="reklaModal" @close="reklaModal = false">
      <template slot="header">
        <h4 class="modal-title">{{ $t("orders.images.title") }} {{ reklaInvoiceNumber }}</h4>
        <md-button class="md-simple md-just-icon md-round modal-default-button" @click="reklaModal = false">
          <md-icon>clear</md-icon>
        </md-button>
      </template>
      <template slot="body">
        <OrderImageGallery v-if="reklaInvoiceNumber" :invoice-number="reklaInvoiceNumber" :allow-upload="false" />
      </template>
      <template slot="footer">
        <md-button class="md-secondary" @click="reklaModal = false">{{ $t("returns.detail.close") }}</md-button>
      </template>
    </modal>

    <modal v-if="photoModal" @close="photoModal = false" class-name="returns-modal-container">
      <template slot="header">
        <h4 class="modal-title">{{ $t("returns.detail.photos") }} – #{{ photoReturnId }}</h4>
        <md-button class="md-simple md-just-icon md-round modal-default-button" @click="photoModal = false">
          <md-icon>clear</md-icon>
        </md-button>
      </template>

      <template slot="body">
        <div v-if="photoLoading" class="text-center" style="padding: 24px">
          <md-progress-spinner md-mode="indeterminate" :md-diameter="40"></md-progress-spinner>
        </div>
        <return-photo-gallery v-else :return-id="photoReturnId" :photos="photoList" />
      </template>

      <template slot="footer">
        <md-button class="md-secondary" @click="photoModal = false">{{ $t("returns.detail.close") }}</md-button>
      </template>
    </modal>

    <!-- Dock rechts wie im Lieferplan (P20): Suche, Filter, Schieberegler "inkl. erledigte" -->
    <div class="ret-dock">
      <button class="ret-dock__trigger" :class="{ 'ret-dock__trigger--active': hasSearch }" :title="$t('returns.filter.search')" @click.stop="searchOpen = !searchOpen">
        <md-icon>search</md-icon>
      </button>
      <returns-drawer-filter :participants="participants" @apply-filters="applyDrawerFilters" @opened="onDrawerOpened" @closed="drawerOpen = false" />
    </div>

    <div v-if="searchOpen && !drawerOpen" class="ret-dock__panel">
      <!-- Reihenfolge laut Karte (P20.3): Rechnungsnummer, SKU, Allgemein -->
      <md-field md-clearable>
        <label>{{ $t("returns.filter.invoice") }}</label>
        <md-input v-model="filters.invoiceNumber" @input="debouncedReload"></md-input>
      </md-field>
      <md-field md-clearable>
        <label>{{ $t("returns.filter.sku") }}</label>
        <md-input v-model="filters.sku" @input="debouncedReload"></md-input>
      </md-field>
      <md-field md-clearable>
        <label>{{ $t("returns.filter.search") }}</label>
        <md-input v-model="filters.keywords" @input="debouncedReload"></md-input>
      </md-field>
    </div>

    <div v-show="!drawerOpen" class="ret-dock__info">
      <!-- Der Drawer-Filter angelegt/erledigt schlaegt den Schieberegler - dann ist er aus -->
      <md-switch v-model="includeCompleted" :disabled="filters.completedFilter !== null" :title="filters.completedFilter !== null ? $t('returns.filterDrawer.title') : null" @change="reload">{{
        $t("returns.filter.includeCompleted")
      }}</md-switch>
      <span class="ret-dock__count">{{ returns.mdCount }}</span>
    </div>

    <!-- Bestaetigung "erledigt" (Kundenwunsch 18.08., P14.5) -->
    <modal v-if="completeConfirm.show" @close="completeConfirm.show = false" class-name="ret-complete-modal-container">
      <template slot="header">
        <h4 class="modal-title">{{ $t("returns.detail.complete") }} – #{{ completeConfirm.id }}</h4>
        <md-button class="md-simple md-just-icon md-round modal-default-button" @click="completeConfirm.show = false">
          <md-icon>clear</md-icon>
        </md-button>
      </template>
      <template slot="body">
        <p>{{ $t("returns.detail.completeConfirm") }}</p>
      </template>
      <template slot="footer">
        <md-button class="md-secondary mr-1" @click="completeConfirm.show = false">{{ $t("returns.detail.cancel") }}</md-button>
        <md-button class="md-success" :disabled="saving" @click="confirmComplete">{{ $t("returns.detail.save") }}</md-button>
      </template>
    </modal>
  </div>
</template>

<script>
import debounce from "lodash/debounce";
import * as signalR from "@microsoft/signalr";
import { Modal, MdPagination, UserAvatar } from "@/components";
import config from "@/config";
import TokenService from "@/services/token.service";
import ReturnsService from "@/services/returns.service";
import { hasPermission } from "@/mixins/auth";
import { openAfterbuyInvoiceSearch } from "@/helpers/afterbuy";
import { marketplaceInfo, marketplaceOrderUrl, shopifyStoreUrlForOrder, carrierInfo, carrierOptions } from "@/helpers/marketplaces";
import ReturnPhotoGallery from "./ReturnPhotoGallery.vue";
import ReturnsDrawerFilter from "./ReturnsDrawerFilter.vue";
import OrderImageGallery from "@/components/Orders/OrderImageGallery.vue";

// Lagerstatus - "erledigt" ist seit 18.08. ein eigener, allgemeiner Status (CompletedOn)

export default {
  name: "ReturnsPage",
  components: { Modal, MdPagination, ReturnPhotoGallery, ReturnsDrawerFilter, UserAvatar, OrderImageGallery },
  data() {
    return {
      returns: { mdCount: 0, mdPage: 1, mdData: [] },
      // Gleiche Symbole wie im mobilen Erfassungs-Wizard (ReturnCapture.vue)
      typeIcons: { customer_return: "assignment_return", undeliverable: "wrong_location", amazon_fba: "inventory", other: "help_outline" },
      statusIcons: { captured: "move_to_inbox", assigned: "link", checked: "playlist_add_check", booked: "assignment_turned_in" },
      filters: {
        keywords: "",
        invoiceNumber: "",
        sku: "",
        status: null,
        returnType: null,
        marketplace: null,
        carrier: null,
        createdById: null,
        completedById: null,
        createdFrom: null,
        createdTo: null,
        completedFrom: null,
        completedTo: null,
        completedFilter: null,
        page: 1,
        pageSize: 20,
      },
      // Standardansicht: nur "angelegt" (P20.1); der Schieberegler nimmt die erledigten dazu
      includeCompleted: false,
      drawerOpen: false,
      searchOpen: false,
      participants: { createdBy: [], completedBy: [] },
      completeConfirm: { show: false, id: null, fromDetail: false },
      reklaModal: false,
      reklaInvoiceNumber: null,
      photoModal: false,
      photoLoading: false,
      photoReturnId: null,
      photoList: [],
      detailModal: false,
      detailLoading: false,
      detail: {},
      notesDraft: "",
      restockModal: false,
      restockRows: [],
      bookingLocations: [],
      bookingWarning: false,
      assignModal: false,
      search: { referenceNumber: "", returnTracking: "", senderName: "", senderZipcode: "" },
      candidates: [],
      diagnosis: [],
      searched: false,
      matching: false,
      // null | "label" | "ean"
      extracting: null,
      extractHint: null,
      duplicates: [],
      saving: false,
    };
  },
  computed: {
    hasSearch() {
      return !!(this.filters.keywords || this.filters.invoiceNumber || this.filters.sku);
    },
    canEdit() {
      return hasPermission("Write", "Returns", "Edit");
    },
    canBook() {
      return hasPermission("Write", "Returns", "Book");
    },
    canComplete() {
      return hasPermission("Write", "Returns", "Complete");
    },
    carrierList() {
      return carrierOptions();
    },
    hasExactHit() {
      return this.diagnosis.some((d) => d.outcome === "exact_hit");
    },
    duplicateList() {
      return this.duplicates.map((d) => "#" + d.id + (d.bookedOn ? " (" + this.$t("returns.duplicate.booked") + ")" : "")).join(", ");
    },
    openRestockRows() {
      // Schrott (S) und Benutzt (U) werden nie eingebucht (P10.2)
      return this.restockRows.filter((r) => !r.warehouseInventoryId && r.condition !== "S" && r.condition !== "U");
    },
    timeline() {
      const d = this.detail;
      return [
        { on: d.createdOn, by: d.createdBy, icon: "photo_camera", label: this.$t("returns.statuses.captured") },
        { on: d.assignedOn, by: d.assignedBy, icon: "link", label: this.$t("returns.statuses.assigned") },
        { on: d.checkedOn, by: d.checkedBy, icon: "fact_check", label: this.$t("returns.statuses.checked") },
        { on: d.bookedOn, by: d.bookedBy, icon: "inventory", label: this.$t("returns.statuses.booked") },
        { on: d.completedOn, by: d.completedBy, icon: "task_alt", label: this.$t("returns.statuses.completed") },
      ].filter((e) => e.on);
    },
  },
  created() {
    ReturnsService.participants()
      .then((p) => {
        this.participants = p || { createdBy: [], completedBy: [] };
      })
      .catch(() => {});
    this.debouncedReload = debounce(this.reload, 350);
    this.getReturns();
  },
  mounted() {
    this._signalrConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${config.baseURLApi}/hubs/shippingplan`, {
        accessTokenFactory: () => (TokenService.getToken() || "").replace(/^Bearer\s+/i, ""),
      })
      .withAutomaticReconnect()
      .build();

    this._signalrConnection.on("ReturnUpdated", (id) => {
      this.getReturns();
      if (this.detailModal && this.detail.id === id) this.loadDetail(id);
    });

    this._signalrConnection.start().catch((err) => console.error("SignalR:", err));
  },
  beforeDestroy() {
    if (this._signalrConnection) this._signalrConnection.stop();
  },
  methods: {
    initials(name) {
      return (name || "")
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0].toUpperCase())
        .join("");
    },
    openReklaImages(item) {
      this.reklaInvoiceNumber = item.invoiceNumber;
      this.reklaModal = true;
    },
    async openPhotos(item) {
      this.photoReturnId = item.id;
      this.photoList = [];
      this.photoLoading = true;
      this.photoModal = true;
      try {
        this.photoList = await ReturnsService.photos(item.id);
      } catch (err) {
        this.notifyError(err, "load_failed");
      }
      this.photoLoading = false;
    },
    // Haken in der Uebersicht nur, wenn wirklich jede einbuchbare Position eingebucht ist
    // (Schrott und Benutzt werden nicht eingebucht und zaehlen nicht)
    allBooked(item) {
      const relevant = (item.items || []).filter((i) => i.condition !== "S" && i.condition !== "U");
      return !!(relevant.length && relevant.every((i) => i.warehouseInventoryId));
    },
    bookedLocations(item) {
      const locs = this.bookedItems(item)
        .map((s) => s.storageLocation)
        .filter(Boolean);
      return [...new Set(locs)];
    },
    bookedItems(item) {
      return (item.items || []).filter((i) => i.warehouseInventoryId);
    },
    wmsLocations(item) {
      return [
        ...new Set(
          this.bookedItems(item)
            .map((i) => i.storageLocation)
            .filter(Boolean)
        ),
      ];
    },
    conditionLabel(c) {
      if (c === "B") return this.$t("returns.items.conditionB");
      if (c === "S") return this.$t("returns.items.conditionS");
      if (c === "U") return this.$t("returns.items.conditionU");
      return this.$t("returns.items.conditionA");
    },
    // Lange Nummern kuerzen - der volle Wert steht im Mouseover und haengt am Kopier-Symbol
    short(value, max = 14) {
      const s = String(value == null ? "" : value);
      return s.length > max ? s.slice(0, max) + "…" : s;
    },
    async copy(value) {
      if (!value) return;
      const text = String(value);
      try {
        await navigator.clipboard.writeText(text);
      } catch (err) {
        // Chrome verweigert die Clipboard-API ohne Fokus/Nutzergeste - dann der alte Weg
        if (!this.copyFallback(text)) {
          this.notify("danger", this.$t("returns.table.copyFailed"));
          return;
        }
      }
      this.notify("success", this.$t("returns.table.copied"));
    },
    copyFallback(text) {
      const helper = document.createElement("textarea");
      helper.value = text;
      helper.setAttribute("readonly", "");
      helper.style.position = "fixed";
      helper.style.opacity = "0";
      document.body.appendChild(helper);
      helper.select();
      let ok = false;
      try {
        ok = document.execCommand("copy");
      } catch (err) {
        ok = false;
      }
      document.body.removeChild(helper);
      return ok;
    },
    notify(type, message) {
      this.$notify({ timeout: 3000, message, icon: type === "danger" ? "error" : "check", horizontalAlign: "right", verticalAlign: "top", type });
    },
    notifyError(err, fallback) {
      const reason = (err && err.response && err.response.data && err.response.data.reason) || fallback;
      const key = "returns.errors." + reason;
      this.notify("danger", this.$te(key) ? this.$t(key) : this.$t("returns.errors." + fallback));
    },
    applyDrawerFilters(f) {
      this.filters.completedFilter = f.completed;
      this.filters.status = f.status;
      this.filters.returnType = f.returnType;
      this.filters.marketplace = f.marketplace;
      this.filters.carrier = f.carrier;
      this.filters.createdById = f.createdById;
      this.filters.completedById = f.completedById;
      this.filters.createdFrom = f.createdFrom;
      this.filters.createdTo = f.createdTo;
      this.filters.completedFrom = f.completedFrom;
      this.filters.completedTo = f.completedTo;
      this.reload();
    },
    onDrawerOpened() {
      this.drawerOpen = true;
      this.searchOpen = false;
    },
    reload() {
      this.filters.page = 1;
      this.getReturns();
    },
    updatePagination(page, pageSize) {
      this.filters.page = page || 1;
      this.filters.pageSize = pageSize;
      this.getReturns();
    },
    async getReturns() {
      // Drawer-Auswahl (angelegt/erledigt) schlaegt den Schieberegler; ohne beides gilt "nur angelegt" (P20.1)
      const completed = this.filters.completedFilter !== null ? this.filters.completedFilter : this.includeCompleted ? undefined : false;
      const payload = await ReturnsService.list({
        keywords: this.filters.keywords || undefined,
        invoiceNumber: this.filters.invoiceNumber || undefined,
        sku: this.filters.sku || undefined,
        status: this.filters.status || undefined,
        returnType: this.filters.returnType || undefined,
        marketplace: this.filters.marketplace || undefined,
        carrier: this.filters.carrier || undefined,
        createdById: this.filters.createdById || undefined,
        completedById: this.filters.completedById || undefined,
        createdFrom: this.filters.createdFrom || undefined,
        createdTo: this.filters.createdTo || undefined,
        completedFrom: this.filters.completedFrom || undefined,
        completedTo: this.filters.completedTo || undefined,
        completed,
        pageIndex: (this.filters.page || 1) - 1,
        pageSize: this.filters.pageSize,
      });
      this.returns = Object.assign({}, this.returns, {
        mdData: payload.data || [],
        mdCount: payload.total || 0,
        mdPage: this.filters.page,
      });
    },
    async openDetail(item) {
      this.detail = {};
      this.detailModal = true;
      await this.loadDetail(item.id);
    },
    async loadDetail(id) {
      this.detailLoading = true;
      try {
        this.detail = await ReturnsService.get(id);
        this.notesDraft = this.detail.notes || "";
      } catch (err) {
        this.notifyError(err, "load_failed");
        this.detailModal = false;
      } finally {
        this.detailLoading = false;
      }
    },
    async saveNotes() {
      this.saving = true;
      try {
        await ReturnsService.update(this.detail.id, { notes: this.notesDraft });
        this.detail.notes = this.notesDraft;
        this.notify("success", this.$t("returns.detail.notesSaved"));
        this.getReturns();
      } catch (err) {
        this.notifyError(err, "save_failed");
      } finally {
        this.saving = false;
      }
    },
    marketplace(item) {
      return marketplaceInfo(item && item.marketplace);
    },
    carrier(item) {
      return carrierInfo(item && item.carrier);
    },
    async saveCarrier() {
      try {
        await ReturnsService.update(this.detail.id, { carrier: this.detail.carrier || "" });
        this.notify("success", this.$t("returns.detail.saved"));
        this.getReturns();
      } catch (err) {
        this.notifyError(err, "save_failed");
      }
    },
    orderUrl(marketplace, nr) {
      return marketplaceOrderUrl(marketplace, nr);
    },
    shopifyUrl(nr) {
      return shopifyStoreUrlForOrder(nr);
    },
    openInvoiceSearch(nr) {
      openAfterbuyInvoiceSearch(nr);
    },
    askComplete(id, fromDetail) {
      this.completeConfirm = { show: true, id, fromDetail };
    },
    async confirmComplete() {
      const { id, fromDetail } = this.completeConfirm;
      this.saving = true;
      try {
        await ReturnsService.complete(id);
        this.notify("success", this.$t("returns.detail.completed"));
        this.completeConfirm.show = false;
        // Aus dem Detail heraus schliessen sich beide Lightboxen (Kundenwunsch 18.08., P14.5)
        if (fromDetail) this.closeDetail();
        this.getReturns();
      } catch (err) {
        this.notifyError(err, "save_failed");
      } finally {
        this.saving = false;
      }
    },
    closeDetail() {
      this.detailModal = false;
      this.detail = {};
    },

    // --- Zuordnung ---
    openAssign() {
      this.search = {
        referenceNumber: this.detail.referenceNumber || "",
        returnTracking: this.detail.returnTrackingNumber || "",
        senderName: this.detail.senderName || "",
        senderZipcode: this.detail.senderZipcode || "",
      };
      this.candidates = [];
      this.diagnosis = [];
      this.duplicates = [];
      this.searched = false;
      this.assignModal = true;
      if (this.search.returnTracking) this.checkDuplicates(this.search.returnTracking);
      if (this.search.referenceNumber || this.search.returnTracking || (this.search.senderName && this.search.senderZipcode)) {
        this.findCandidates();
      }
    },
    // Foto per Vision-Modell auslesen; die gelesenen Werte landen in den Suchfeldern,
    // die Kandidaten kommen aus der Re-Validierung des Servers.
    // kind: "label" (Versandlabel) oder "ean" (EAN-Sticker).
    async runExtract(kind) {
      const istEan = kind === "ean";
      const fehlerPfad = istEan ? "returns.extract.eanErrors." : "returns.extract.errors.";
      this.extracting = kind;
      this.extractHint = null;
      try {
        const res = await ReturnsService.extract(this.detail.id, kind);
        if (!res.success) {
          const key = fehlerPfad + res.error;
          this.extractHint = this.$te(key) ? this.$t(key) : this.$t(fehlerPfad + "failed");
          return;
        }

        const parts = [];
        if (istEan) {
          const e = res.ean || {};
          if (e.sku) parts.push(e.sku);
          if (e.ean) parts.push("EAN " + e.ean);
          if (e.productName) parts.push(e.productName);
          this.extractHint = this.$t("returns.extract.readEan", { info: parts.join(" · ") });
          if (e.sku && !e.skuKnown) this.extractHint += " – " + this.$t("returns.extract.skuUnknown", { sku: e.sku });
          if (e.skuFromGtin) this.extractHint += " – " + this.$t("returns.extract.skuFromGtin");
        } else {
          const p = res.proposal || {};
          if (p.invoiceNumber || p.referenceNumber) this.search.referenceNumber = p.invoiceNumber || p.referenceNumber;
          if (p.returnTracking) this.search.returnTracking = p.returnTracking;
          if (p.sender && p.sender.name) this.search.senderName = p.sender.name;
          if (p.sender && p.sender.zipcode) this.search.senderZipcode = p.sender.zipcode;

          if (p.suggestedReturnType) parts.push(this.$t("returns.types." + p.suggestedReturnType));
          if (p.skuLine) parts.push(p.skuLine);
          this.extractHint = parts.length ? this.$t("returns.extract.read", { info: parts.join(" · ") }) : null;
          // Die API verwirft einen Absender auf die eigene Firmenanschrift (Empfaengerblock des Labels)
          if (p.senderIsOwnAddress) {
            const warn = this.$t("returns.extract.ownAddress");
            this.extractHint = this.extractHint ? this.extractHint + " – " + warn : warn;
          }
          this.duplicates = res.duplicates || [];
        }

        if (res.match) {
          this.candidates = res.match.candidates || [];
          this.diagnosis = res.match.diagnosis || [];
          this.searched = true;
        }
      } catch (err) {
        this.extractHint = this.$t(fehlerPfad + "failed");
      } finally {
        this.extracting = null;
      }
    },
    async checkDuplicates(trackingNumber) {
      try {
        this.duplicates = await ReturnsService.duplicates(trackingNumber, this.detail.id);
      } catch (err) {
        this.duplicates = [];
      }
    },
    async findCandidates() {
      this.matching = true;
      try {
        const res = await ReturnsService.candidates(this.detail.id, {
          referenceNumber: this.search.referenceNumber || undefined,
          returnTracking: this.search.returnTracking || undefined,
          senderName: this.search.senderName || undefined,
          senderZipcode: this.search.senderZipcode || undefined,
        });
        this.candidates = res.candidates || [];
        this.diagnosis = res.diagnosis || [];
        this.searched = true;
        if (this.search.returnTracking) this.checkDuplicates(this.search.returnTracking);
      } catch (err) {
        this.notifyError(err, "load_failed");
      } finally {
        this.matching = false;
      }
    },
    scoreClass(score) {
      if (score >= 85) return "ret-score--exact";
      if (score >= 50) return "ret-score--fuzzy";
      return "ret-score--weak";
    },
    async assign(candidate) {
      this.saving = true;
      try {
        await ReturnsService.assign(this.detail.id, {
          orderId: candidate.orderId || null,
          afterbuyOrderId: candidate.afterbuyOrderId || null,
        });
        this.notify("success", this.$t("returns.match.assigned"));
        this.assignModal = false;
        await this.loadDetail(this.detail.id);
        this.getReturns();
      } catch (err) {
        this.notifyError(err, "load_failed");
      } finally {
        this.saving = false;
      }
    },

    // --- Einbuchen ---
    async openRestock() {
      try {
        const preview = await ReturnsService.bookingPreview(this.detail.id);
        this.bookingLocations = preview.locations || [];
        this.bookingWarning = !!preview.alreadyBookedWarning;
        const def = this.bookingLocations.find((l) => l.isDefault) || this.bookingLocations[0];
        this.restockRows = (this.detail.items || []).map((i) => ({ ...i, storageLocationId: i.storageLocationId || (def && def.id) }));
        this.restockModal = true;
      } catch (err) {
        this.notifyError(err, "load_failed");
      }
    },
    async book() {
      this.saving = true;
      try {
        await ReturnsService.book(
          this.detail.id,
          this.openRestockRows.map((r) => ({ returnItemId: r.id, storageLocationId: r.storageLocationId }))
        );
        this.notify("success", this.$t("returns.booking.booked"));
        this.restockModal = false;
        await this.loadDetail(this.detail.id);
        this.getReturns();
      } catch (err) {
        this.notifyError(err, "invalid_status");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
/* Dock rechts (P20) - gleiche Optik wie im Lieferplan */
.ret-dock {
  position: fixed;
  top: 110px;
  right: 0;
  /* Ueber Suchpanel (1041) und Info-Box (1040): der Filter-Drawer lebt IN diesem Container
     und wuerde sonst von beiden ueberlagert (Kundenscreenshot 20.08.) */
  z-index: 1048;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.ret-dock ::v-deep .ret-dock__trigger,
.ret-dock__trigger {
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 8px 0 0 8px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}
.ret-dock ::v-deep .ret-dock__trigger--active,
.ret-dock__trigger--active {
  background: #7f915a;

  .md-icon {
    color: #fff !important;
  }
}
.ret-dock ::v-deep .ret-dock__badge {
  position: absolute;
  top: 2px;
  right: 4px;
  background: #f44336;
  color: #fff;
  border-radius: 50%;
  min-width: 16px;
  height: 16px;
  font-size: 10px;
  line-height: 16px;
  text-align: center;
}
.ret-dock__panel {
  position: fixed;
  top: 110px;
  right: 52px;
  z-index: 1041;
  width: 280px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.22);
  padding: 8px 16px;
}
.ret-dock__info {
  position: fixed;
  bottom: 14px;
  right: 0;
  z-index: 1040;
  background: #fff;
  border-radius: 8px 0 0 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
  padding: 2px 14px;
  display: flex;
  align-items: center;
  gap: 10px;
}
.ret-dock__count {
  font-size: 12px;
  color: #666;
}

/* Erledigte Vorgaenge angenehm gruen hinterlegen (Kundenwunsch 18.08., P14.2) */
.ret-row--completed,
.ret-row--completed:nth-child(even),
.ret-row--completed:hover {
  background: #eff4e6 !important;
}
.ret-mp {
  display: inline-block;
  min-width: 34px;
  padding: 2px 6px;
  border-radius: 4px;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  text-align: center;
  letter-spacing: 0.3px;
}
.ret-carrier-select {
  border: none;
  border-bottom: 1px solid #ccc;
  background: transparent;
  padding: 2px 0;
  font-size: 13px;
}
.ret-idline {
  color: #777;
  font-size: 12px;
}
.ret-gstatus {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}
.ret-gstatus--created {
  background: #eceff1;
  color: #455a64;
}
.ret-gstatus--completed {
  background: #7f915a;
  color: #fff;
}
.ret-status {
  display: inline-block;
  font-size: 12px;
  border-radius: 10px;
  padding: 2px 10px;
  background: #eeeeee;
  color: #616161;
  margin-left: 6px;

  &--booked {
    background: #f1f5e8;
    color: #7f915a;
  }
  &--captured {
    background: #fff3e0;
    color: #ef6c00;
  }
  &--assigned,
  &--checked {
    background: #e3f2fd;
    color: #1565c0;
  }
  &--completed {
    background: #ede7f6;
    color: #4527a0;
  }
}

.ret-warn {
  color: #f44336 !important;
  font-size: 18px !important;
}

.ret-ok {
  color: #7f915a !important;
}

.ret-ok-text {
  color: #7f915a;
  font-size: 13px;
}

// Uebersichtszellen: mehrzeilig mit Symbol und Tooltip, wie in der Lieferplan-Tabelle
.ret-sku {
  white-space: nowrap;
  line-height: 1.5;
}

.ret-cond {
  display: inline-block;
  min-width: 16px;
  text-align: center;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 500;
  padding: 0 4px;
  margin-left: 4px;

  &--a {
    background: #f1f5e8;
    color: #7f915a;
  }
  &--b {
    background: #fff3e0;
    color: #ef6c00;
  }
  &--s {
    background: #eceff1;
    color: #546e7a;
  }
  &--u {
    background: #ffebee;
    color: #c62828;
  }
}

/* Flaechenfuellung im Zurueck-Bereich (P15): Neuware gruen, B-Ware orange, Schrott grau, Benutzt rot */
.ret-sku--filled {
  padding: 2px 6px;
  border-radius: 4px;
  margin-bottom: 2px;
}
.ret-fill--a {
  background: #f1f5e8;
}
.ret-fill--b {
  background: #ffe9cf;
}
.ret-fill--s {
  background: #e4e7ea;
}
.ret-fill--u {
  background: #ffdfdf;
}

.ret-wms {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  cursor: default;

  .md-icon {
    font-size: 16px !important;
  }
}

.ret-note {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: default;
}

.ret-notesfield {
  margin-top: 0;
}

.ret-more {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #757575;
  cursor: default;

  .md-icon {
    font-size: 16px !important;
  }
}

// Das Theme zentriert Tabellenzellen je nach Inhalt - hier bewusst hart linksbuendig,
// sonst wandern Referenz und Sendungsnummer in derselben Spalte unterschiedlich weit nach rechts.
// Zebrastreifen wie in den uebrigen Tabellen - dasselbe Grau wie .table-striped im Theme
// (assets/scss/md/_tables.scss). md-table rendert echte tr in einem tbody, deshalb nth-of-type.
::v-deep .md-table-content tbody > tr.md-table-row:nth-of-type(odd) {
  background-color: #f9f9f9;
}

// Kompakte Zellen: die vue-material-Polster (24/32px) sprengen bei dieser Spaltenzahl die
// Seitenbreite - rechte Spalten liefen aus dem Bild (Kundenscreenshot 21.08.)
::v-deep .md-table-cell {
  font-size: 13px;
}
// !important: vue-material setzt fuer erste/letzte Spalten spezifischere Padding-Regeln
::v-deep .md-table-cell-container {
  padding: 6px 8px !important;
}

// Absender: feste Breite, Zeilen mit Ellipsis - volle Anschrift im Tooltip
.ret-sender {
  max-width: 150px;

  &__line {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
// Header duerfen umbrechen ("Lager Status" zweizeilig) - vue-materials nowrap+28px-Hoehe
// hielt die Spalten sonst auf Ueberschriften-Breite, obwohl der Inhalt nur ein Icon ist
::v-deep .md-table-head-label {
  padding-right: 8px !important;
  padding-left: 8px !important;
  white-space: normal;
  height: auto;
  line-height: 1.25;
  font-size: 11px;
}

// Reicht die Fensterbreite trotzdem nicht, scrollt die Tabelle IN der Karte -
// nie die ganze Seite (zweiter Kundenscreenshot 21.08.)
::v-deep .md-table-content {
  overflow-x: auto;
}

// Status als Icon (Kompakt-Konvention), Farben wie die bisherigen Chips
.ret-statusicon {
  font-size: 20px !important;
  cursor: default;

  &--created {
    color: #757575 !important;
  }
  &--completed {
    color: #4527a0 !important;
  }
  &--captured {
    color: #ef6c00 !important;
  }
  &--assigned,
  &--checked {
    color: #1565c0 !important;
  }
  &--booked {
    color: #7f915a !important;
  }
}

.ret-refcell ::v-deep .md-table-cell-container {
  text-align: left;
}

.ret-refline {
  display: flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
  line-height: 1.6;
  position: relative; // Anker fuer das absolut liegende Kopier-Symbol
  padding-right: 14px; // schmale Reserve, damit das Symbol die letzte Ziffer nicht verdeckt

  // Feste Symbolspalte: die Nummern beider Zeilen beginnen dadurch an derselben Kante.
  // margin: 0 ist Pflicht - das Theme setzt .md-icon auf "margin: auto" und zentriert damit
  // die ganze Flex-Zeile, wodurch die Nummern je nach Textlaenge verrutschen.
  .md-icon {
    flex: 0 0 18px;
    width: 18px;
    min-width: 18px;
    margin: 0 !important;
    font-size: 16px !important;
    color: #9e9e9e !important;
  }

  &--muted {
    color: #757575;
  }

  &__val {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    cursor: default;
  }
}

// Kopier-Symbol bleibt unsichtbar, bis die Zeile ueberfahren wird - sonst 40 Icons je Seite.
// Es liegt absolut ueber dem Zeilenende und reserviert KEINE Spaltenbreite mehr (21.08.)
.ret-copy {
  border: 0;
  background: none;
  padding: 0;
  line-height: 1;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s;
  position: absolute;
  right: -4px;
  top: 50%;
  transform: translateY(-50%);

  .md-icon {
    font-size: 14px !important;
    color: #9e9e9e !important;
    margin: 0 !important;
    min-width: 14px;
    width: 14px;
  }

  &:hover .md-icon {
    color: #7f915a !important;
  }
}

::v-deep .md-table-row:hover .ret-copy {
  opacity: 1;
}

.ret-action {
  margin: 0 3px !important;
}

.rekla-icon {
  color: #7f915a !important;
  cursor: pointer;
}

.ret-typeicon {
  color: #616161 !important;
  cursor: default;
}

.ret-photoicon {
  cursor: pointer;
}

// Avatar und Datum untereinander: spart Spaltenbreite, Uhrzeit steckt im Tooltip
.ret-user {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  white-space: nowrap;
  cursor: default;

  &__date {
    font-size: 12px;
    color: #616161;
  }
}

.ret-avatar {
  position: relative;
  flex: 0 0 auto;
  width: 28px;
  min-width: 28px;
  height: 28px;
  margin: 0;
  border-radius: 50%;
  background: #e0e0e0;
  overflow: hidden;
  cursor: default;

  &__initials {
    display: block;
    line-height: 28px;
    text-align: center;
    font-size: 11px;
    font-weight: 500;
    color: #616161;
  }

  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.ret-memoicon {
  font-size: 17px;
  color: #ff9800;
  vertical-align: middle;
  margin-left: 4px;
  cursor: default;
}

// Tooltip-Inhalte: das Theme setzt die Schrift klein, Tabellen brauchen etwas Luft
.ret-tip {
  border-collapse: collapse;

  td {
    padding: 1px 6px 1px 0;
    white-space: nowrap;
  }
}

.ret-tip-memo {
  max-width: 320px;
  white-space: pre-line;
  text-align: left;
}

.ret-detail {
  text-align: left;

  &__heading {
    font-size: 15px;
    font-weight: 500;
    margin: 18px 0 8px;
  }

  &__table {
    font-size: 14px;

    th {
      text-align: left;
      font-weight: 400;
      color: #9e9e9e;
      // feste Beschriftungsspalte: sonst zieht die Tabelle Label und Wert
      // auf breiten Bildschirmen weit auseinander
      width: 130px;
      vertical-align: top;
      padding: 3px 12px 3px 0;
    }
    td {
      padding: 3px 0;
    }
  }

  &__memo {
    background: #fff3e0;
    border-radius: 6px;
    padding: 8px 10px;
    font-size: 13px;
    color: #6d4c41;
    white-space: pre-line;
    margin-top: 10px;
  }

  &__empty {
    color: #9e9e9e;
    font-size: 13px;
  }
}

.ret-timeline {
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    // Raster statt auto-Rand: Schritt und Zeitstempel stehen dicht beieinander,
    // egal wie breit der Dialog gerade ist
    display: grid;
    grid-template-columns: 26px 110px auto;
    align-items: center;
    gap: 10px;
    padding: 4px 0;
    font-size: 14px;

    .md-icon {
      font-size: 20px !important;
      color: #9e9e9e !important;
    }

    small {
      color: #9e9e9e;
    }
  }
}

.ret-items {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  text-align: left;
  table-layout: fixed;

  th {
    color: #9e9e9e;
    font-weight: 400;
    border-bottom: 1px solid #eee;
    padding: 6px 12px 6px 0;
  }
  td {
    padding: 6px 12px 6px 0;
    border-bottom: 1px solid #f5f5f5;
    vertical-align: middle;
    overflow-wrap: anywhere;
  }

  // Ohne feste Breiten verteilt der Browser die Spalten gleichmaessig ueber die
  // volle Dialogbreite - Menge und Zustand landen dann weit auseinander.
  th:nth-child(1),
  td:nth-child(1) {
    width: 26%;
  }
  th:nth-child(2),
  td:nth-child(2) {
    width: 90px;
  }
  th:nth-child(3),
  td:nth-child(3) {
    width: 110px;
  }
  th:nth-child(4),
  td:nth-child(4) {
    width: 26%;
  }
}

.ret-cands {
  table-layout: auto;

  th:nth-child(n),
  td:nth-child(n) {
    width: auto;
  }
}

.ret-cands td {
  vertical-align: top;
}

.ret-score {
  display: inline-block;
  border-radius: 10px;
  padding: 2px 9px;
  color: #fff;
  font-weight: 600;
  font-size: 12px;

  &--exact {
    background: #7f915a;
  }
  &--fuzzy {
    background: #ff9800;
  }
  &--weak {
    background: #9e9e9e;
  }
}

.ret-field {
  display: inline-block;
  font-size: 11px;
  border-radius: 8px;
  padding: 1px 7px;
  margin: 0 3px 3px 0;

  &--ok {
    background: #f1f5e8;
    color: #7f915a;
  }
  &--bad {
    background: #ffebee;
    color: #c62828;
  }
}

.ret-diag {
  background: #fff8e1;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 13px;
  margin: 12px 0;
  text-align: left;

  &--hit {
    background: #f1f5e8;
  }

  ul {
    margin: 4px 0 0;
    padding-left: 18px;
  }
}

.ret-extract-hint {
  background: #f1f5e8;
  color: #7f915a;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
  margin: 8px 0 0;
  text-align: left;
}

.ret-dup {
  background: #fff3e0;
  color: #e65100;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 13px;
  margin: 8px 0 0;
  text-align: left;
}

.ret-warnbox {
  background: #ffebee;
  color: #c62828;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
}

.ret-zoom {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.9);
  z-index: 10002;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: zoom-out;

  img {
    max-width: 92%;
    max-height: 92%;
  }
}
</style>
